import {
  Box,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Select,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  OutlinedInput,
} from "@mui/material";

import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import DoubleArrowLeft from "@mui/icons-material/KeyboardDoubleArrowLeft";
import DoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
  getCompleteWorkDiaryAction,
  getWorkDiaryTypeOptionsAction,
  setUpdateWorkDiaryBookingStatusAction,
} from "../../../../store/reducers/workDiarySlice";

import RouteGoogleOptimiseModal from "./RouteGoogleOptimiseModal";

interface DiaryHeaderProps {
  filteredData: any;
  setFilteredData: any;
}

const DiaryHeader: React.FC<DiaryHeaderProps> = ({
  filteredData,
  setFilteredData,
}) => {
  const dispatch = useDispatch();
  const { userProfile } = useSelector((state: any) => state?.user);
  const [routeOptimiseOpen, setRouteOptimiseOpen] = useState(false);

  const { workDiaryTypeOptions, isBookingStatusUpdated } = useSelector(
    (state: any) => state?.workDiary,
  );

  /** -----------------------------
   * API CALL
   ------------------------------*/

  useEffect(() => {
    dispatch(getWorkDiaryTypeOptionsAction());
    dispatch(getCompleteWorkDiaryAction(filteredData));
  }, [dispatch, filteredData]);

  useEffect(() => {
    if (isBookingStatusUpdated) {
      dispatch(getCompleteWorkDiaryAction(filteredData));
      dispatch(setUpdateWorkDiaryBookingStatusAction(false));
    }
  }, [isBookingStatusUpdated]);

  /** -----------------------------
   * SHIFT RANGE
   ------------------------------*/

  const shiftRange = (days: number) => {
    const newStart = dayjs(filteredData.startDateRange).add(days, "day");
    const newEnd = dayjs(filteredData.endDateRange).add(days, "day");

    setFilteredData({
      ...filteredData,
      startDateRange: newStart.format("YYYY-MM-DD"),
      endDateRange: newEnd.format("YYYY-MM-DD"),
    });
  };

  /** -----------------------------
   * START DATE CHANGE
   ------------------------------*/

  const handleStartDateChange = (value: string) => {
    const diff = dayjs(filteredData.endDateRange).diff(
      dayjs(filteredData.startDateRange),
      "day",
    );

    setFilteredData({
      ...filteredData,
      startDateRange: value,
      endDateRange: dayjs(value).add(diff, "day").format("YYYY-MM-DD"),
    });
  };

  /** -----------------------------
   * DEFAULT FILTER
   ------------------------------*/

  const monday = dayjs().startOf("week");
  const sunday = monday.add(6, "day");

  const defaultFilter = {
    search: "",
    startDateRange: monday.format("YYYY-MM-DD"),
    endDateRange: sunday.format("YYYY-MM-DD"),
    taskType: "",
    actionType: "professional",
  };

  /** -----------------------------
   * TASK TYPE (MULTI-SELECT, COMMA SEPARATED)
   ------------------------------*/

  // filteredData.taskType is kept as a comma-separated string (e.g. "Cleaning,Repair")
  // Select (multiple) needs an array, so we derive it here and convert back on change.
  const selectedTaskTypes: string[] = filteredData.taskType
    ? filteredData.taskType.split(",")
    : [];

  const handleTaskTypeChange = (e: any) => {
    const value = e.target.value;
    // MUI can give either an array or a comma-separated string depending on event source
    const selectedValues = typeof value === "string" ? value.split(",") : value;

    setFilteredData({
      ...filteredData,
      taskType: selectedValues.join(","),
    });
  };

  return (
    <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
      {/* 28 days back */}
      <IconButton onClick={() => shiftRange(-28)}>
        <DoubleArrowLeft />
      </IconButton>

      {/* 7 days back */}
      <IconButton onClick={() => shiftRange(-7)}>
        <ChevronLeft />
      </IconButton>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Start Date"
          value={
            filteredData.startDateRange
              ? dayjs(filteredData.startDateRange)
              : null
          }
          onChange={(newValue) => {
            if (!newValue) return;

            handleStartDateChange(newValue.format("YYYY-MM-DD"));
          }}
          format={userProfile?.dateFormat || "DD/MM/YYYY"}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: 160,
                "& .MuiInputBase-root": {
                  height: 32,
                  fontSize: 13,
                },
                "& .MuiInputLabel-root": {
                  fontSize: 13,
                },
              },
            },
          }}
        />

        <DatePicker
          label="End Date"
          minDate={
            filteredData.startDateRange
              ? dayjs(filteredData.startDateRange)
              : undefined
          }
          value={
            filteredData.endDateRange ? dayjs(filteredData.endDateRange) : null
          }
          format={userProfile?.dateFormat || "DD/MM/YYYY"}
          onChange={(newValue: any) => {
            if (!newValue) return;

            setFilteredData({
              ...filteredData,
              endDateRange: newValue.format("YYYY-MM-DD"),
            });
          }}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: 160,
                "& .MuiInputBase-root": {
                  height: 32,
                  fontSize: 13,
                },
                "& .MuiInputLabel-root": {
                  fontSize: 13,
                },
              },
            },
          }}
        />
      </LocalizationProvider>

      {/* 7 days forward */}
      <IconButton onClick={() => shiftRange(7)}>
        <ChevronRight />
      </IconButton>

      {/* 28 days forward */}
      <IconButton onClick={() => shiftRange(28)}>
        <DoubleArrowRight />
      </IconButton>

      <Typography fontWeight={600} color="#009688">
        Work Diary
      </Typography>

      {/* TASK TYPE FILTER - MULTI SELECT WITH CHECKBOXES */}
      <FormControl
        size="small"
        sx={{
          width: 220,
          "& .MuiInputBase-root": {
            height: 32,
            fontSize: 13,
          },
          "& .MuiInputLabel-root": {
            fontSize: 13,
          },
        }}
      >
        <InputLabel id="task-type-label">Type of Task</InputLabel>
        <Select
          labelId="task-type-label"
          multiple
          value={selectedTaskTypes}
          onChange={handleTaskTypeChange}
          input={<OutlinedInput label="Type of Task" />}
          renderValue={(selected: string[]) =>
            selected.length ? selected.join(", ") : "All"
          }
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {workDiaryTypeOptions?.map((item: any, idx: number) => (
            <MenuItem
              key={idx}
              value={item}
              sx={{
                minHeight: 28,
                py: 0.25,
                px: 1,
              }}
            >
              <Checkbox
                checked={selectedTaskTypes.indexOf(item) > -1}
                size="small"
                sx={{
                  p: 0.5,
                  mr: 0.5,
                }}
              />
              <ListItemText
                primary={item}
                primaryTypographyProps={{ fontSize: 13 }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        size="small"
        onClick={() => setFilteredData(defaultFilter)}
      >
        Reset Filters
      </Button>

      <Button
        variant="outlined"
        size="small"
        onClick={() => setRouteOptimiseOpen(true)}
      >
        Optimise Route
      </Button>

      {routeOptimiseOpen && (
        <RouteGoogleOptimiseModal
          filteredData={filteredData}
          open={routeOptimiseOpen}
          onClose={() => setRouteOptimiseOpen(false)}
        />
      )}
    </Box>
  );
};

export default DiaryHeader;
